import { mutationOptions, queryOptions } from "@tanstack/react-query";
import { ouraKeys } from "@/constant/queryKeys";
import { apiRequestV2, queryClient } from "@/lib/queryClient";
import { encryptOuraPayloadSecrets } from "@/utils/ouraSecretEncryption";
import type {
  OuraListParams,
  OuraOperation,
  OuraProject,
  OuraProjectCollectionDetail,
  OuraProjectDetail,
  OuraProtocolConfig,
  OuraOnchainTrait,
  OuraTransactionResult,
  OuraTrait,
  OuraTraitDetail,
  OuraVaultInfo,
  PaginatedResponse,
} from "@/types/oura";

function toQuery(params?: OuraListParams) {
  const search = new URLSearchParams();
  Object.entries(params ?? {}).forEach(([key, value]) => {
    if (value !== undefined && value !== "") search.set(key, String(value));
  });
  const query = search.toString();
  return query ? `?${query}` : "";
}

function invalidateOura() {
  queryClient.invalidateQueries({ queryKey: ouraKeys.all });
}

export function getProtocolConfigOptions() {
  return queryOptions({
    queryKey: ouraKeys.protocol,
    queryFn: () => apiRequestV2<OuraProtocolConfig>("GET", "/api/v1/oura/protocol"),
  });
}

export function getOuraProjectsOptions(params: OuraListParams) {
  return queryOptions({
    queryKey: ouraKeys.projects(params),
    queryFn: () =>
      apiRequestV2<PaginatedResponse<OuraProject>>(
        "GET",
        `/api/v1/oura/admin/projects${toQuery(params)}`,
      ),
    placeholderData: (previous) => previous,
  });
}

export function getOuraProjectDetailOptions(projectId?: number | null) {
  return queryOptions({
    queryKey: ouraKeys.projectDetail(projectId),
    queryFn: () =>
      apiRequestV2<OuraProjectDetail>(
        "GET",
        `/api/v1/oura/admin/projects/${projectId}`,
      ),
    enabled: Number.isFinite(projectId),
  });
}

export function getOuraProjectCollectionDetailOptions(projectId?: number | null, collection?: string | null) {
  return queryOptions({
    queryKey: ouraKeys.projectCollectionDetail(projectId, collection),
    queryFn: () =>
      apiRequestV2<OuraProjectCollectionDetail>(
        "GET",
        `/api/v1/oura/admin/projects/${projectId}/collections/${encodeURIComponent(collection ?? "")}`,
      ),
    enabled: Number.isFinite(projectId) && Boolean(collection),
  });
}

export function getOuraTraitsOptions(params: OuraListParams) {
  return queryOptions({
    queryKey: ouraKeys.traits(params),
    queryFn: () =>
      apiRequestV2<PaginatedResponse<OuraTrait>>(
        "GET",
        `/api/v1/oura/admin/traits${toQuery(params)}`,
      ),
    placeholderData: (previous) => previous,
  });
}

export function getOuraTraitDetailOptions(collection?: string | null) {
  return queryOptions({
    queryKey: ouraKeys.traitDetail(collection),
    queryFn: () =>
      apiRequestV2<OuraTraitDetail>(
        "GET",
        `/api/v1/oura/admin/traits/${encodeURIComponent(collection ?? "")}`,
      ),
    enabled: Boolean(collection),
  });
}

export function getOuraTraitOnchainOptions(collection?: string | null) {
  return queryOptions({
    queryKey: ouraKeys.trait(collection ?? ""),
    queryFn: () =>
      apiRequestV2<OuraOnchainTrait>(
        "GET",
        `/api/v1/oura/trait?collection=${encodeURIComponent(collection ?? "")}`,
      ),
    enabled: Boolean(collection),
  });
}

export function getOuraOperationsOptions(params: OuraListParams) {
  return queryOptions({
    queryKey: ouraKeys.operations(params),
    queryFn: () =>
      apiRequestV2<PaginatedResponse<OuraOperation>>(
        "GET",
        `/api/v1/oura/admin/operations${toQuery(params)}`,
      ),
    placeholderData: (previous) => previous,
  });
}

export function getNftVaultOptions(asset: string, collection: string, tokenMint: string) {
  return queryOptions({
    queryKey: ouraKeys.nftVault(asset, collection, tokenMint),
    queryFn: () =>
      apiRequestV2<OuraVaultInfo>(
        "GET",
        `/api/v1/oura/nft/vault?asset=${encodeURIComponent(asset)}&collection=${encodeURIComponent(
          collection,
        )}&token_mint=${encodeURIComponent(tokenMint)}`,
      ),
    enabled: Boolean(asset && collection && tokenMint),
  });
}

export function ouraMutationOptions<TPayload>(method: string, url: string) {
  return mutationOptions<OuraTransactionResult, Error, TPayload>({
    mutationFn: async (data) => apiRequestV2<OuraTransactionResult>(method, url, await encryptOuraPayloadSecrets(data)),
    onSuccess: invalidateOura,
  });
}
